/**
 * Push Notification Settings Component
 * Allows users to enable/disable push notifications
 */

import React, { useState } from 'react';
import { Bell, BellOff, Loader2, CheckCircle2, XCircle, Send } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Alert, AlertDescription } from '../ui/alert';
import usePushNotifications from '../../utils/usePushNotifications';

export default function PushNotificationSettings() {
  const {
    isSupported,
    isEnabled,
    permission,
    loading,
    error,
    enableNotifications,
    disableNotifications,
    sendTestNotification
  } = usePushNotifications();

  const [testStatus, setTestStatus] = useState(null);
  const [testLoading, setTestLoading] = useState(false);

  const handleToggle = async () => {
    if (isEnabled) {
      await disableNotifications();
    } else {
      await enableNotifications();
    }
  };

  const handleTestNotification = async () => {
    setTestLoading(true);
    setTestStatus(null);
    try {
      const result = await sendTestNotification();
      setTestStatus({
        success: true,
        message: `Test notification sent! Success: ${result.success_count}, Failed: ${result.failure_count}`
      });
    } catch (err) {
      setTestStatus({
        success: false,
        message: err.response?.data?.detail || 'Failed to send test notification'
      });
    } finally {
      setTestLoading(false);
    }
  };

  if (!isSupported) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BellOff className="h-5 w-5" />
            Push Notifications
          </CardTitle>
          <CardDescription>
            Push notifications are not supported in this browser
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <XCircle className="h-4 w-4" />
            <AlertDescription>
              Your browser doesn't support push notifications. Try using Chrome, Firefox, or Edge.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Push Notifications
        </CardTitle>
        <CardDescription>
          Get notified about new deals from your favorite stores
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Main toggle */}
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <div className="font-medium">Enable Push Notifications</div>
            <div className="text-sm text-gray-500">
              Receive alerts when new deals are posted
            </div>
          </div>
          <div className="flex items-center gap-2">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            <Switch
              checked={isEnabled}
              onCheckedChange={handleToggle}
              disabled={loading || permission === 'denied'}
            />
          </div>
        </div>

        {/* Permission denied warning */}
        {permission === 'denied' && (
          <Alert variant="destructive">
            <XCircle className="h-4 w-4" />
            <AlertDescription>
              Notifications are blocked. Please enable them in your browser settings.
            </AlertDescription>
          </Alert>
        )}

        {/* Error message */}
        {error && (
          <Alert variant="destructive">
            <XCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Success status */}
        {isEnabled && !loading && (
          <Alert className="border-green-200 bg-green-50">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-700">
              Push notifications are enabled. You'll be notified of new deals!
            </AlertDescription>
          </Alert>
        )}

        {/* Test notification button */}
        {isEnabled && (
          <div className="pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleTestNotification}
              disabled={testLoading}
            >
              {testLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Send className="mr-2 h-4 w-4" />
              )}
              Send Test Notification
            </Button>
            
            {testStatus && (
              <p className={`mt-2 text-sm ${testStatus.success ? 'text-green-600' : 'text-red-600'}`}>
                {testStatus.message}
              </p>
            )}
          </div>
        )}

        {/* Info section */}
        <div className="pt-4 border-t text-sm text-gray-500">
          <p className="font-medium mb-1">What notifications will I receive?</p>
          <ul className="list-disc list-inside space-y-1">
            <li>New deals from stores in your area</li>
            <li>Price drops on items you're interested in</li>
            <li>Deals about to expire</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}
